import Autocomplete, { AutocompleteAction } from '../../inputs/Autocomplete';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { getInitialOptionValue } from '@/common/presentation/utils/autocompleteUtils';
import { AutocompleteInputChangeReason, SxProps, Theme } from '@mui/material';

import { BaseFormikFieldProps } from './types/FormikFieldProps';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';

export type OptionType = {
  value: string | number;
  label: string;
  key?: string;
};

type GetTagProps = (params: { index: number }) => {
  key: number | string;
  'data-tag-index': number;
  tabIndex: number;
  onDelete: (event: React.SyntheticEvent) => void;
};

export type FormikAutocompleteProps<T = string | OptionType, TFormData = Record<string, unknown>> = BaseFormikFieldProps<TFormData> & {
  options: T[];
  helperText?: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  multiple?: boolean;
  clearOnBlur?: boolean;
  clearOnEscape?: boolean;
  disableClearable?: boolean;
  disableCloseOnSelect?: boolean;
  disablePortal?: boolean;
  filterOptions?: (options: T[], state: { inputValue: string }) => T[];
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  loading?: boolean;
  optionsloading?: boolean;
  loadingText?: React.ReactNode;
  noOptionsText?: React.ReactNode;
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode;
  renderTags?: (value: T[], getTagProps: GetTagProps) => React.ReactNode;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  /** Props to be passed to the internal MUI components */
  slotProps?: Record<string, object>;
  /** Custom actions to display at the top of the dropdown */
  actions?: AutocompleteAction[];
  /** Callback fired when the input value changes */
  onInputChange?: (event: React.SyntheticEvent, value: string, reason?: AutocompleteInputChangeReason) => void;
  /** Callback fired when the value changes */
  onChange?: (value: T | T[] | null) => void;
  /** Initial value with both ID and label */
  initialOption?: T;
  /** Current value — pass T[] when multiple=true */
  value?: T | T[] | null;
  /** Whether to focus the input on mount */
  autoFocus?: boolean;
  /** Reference to the input element */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Test ID for testing (pass string e.g. testId="page:tab:field") */
  testId?: string;
  /** @deprecated Use testId instead */
  'data-testid'?: string;
  required?: boolean;
  /** Element to display at the start of the input */
  startAdornment?: React.ReactNode;
  /** Element to display at the end of the input */
  endAdornment?: React.ReactNode;
  /** Placeholder text for the input field */
  placeholder?: string;
  /** Whether to skip validation on blur when an action button is clicked */
  skipValidationOnActionClick?: boolean;
};

/**
 * Autocomplete component integrated with Formik
 *
 * @example
 * ```tsx
 * <FormikAutocomplete<OptionType, FormValues>
 *   id="country"
 *   label="Select a country"
 *   options={[
 *     { value: 'us', label: 'USA' },
 *     { value: 'ca', label: 'Canada' },
 *     { value: 'mx', label: 'Mexico' }
 *   ]}
 *   getOptionLabel={(option) => option.label}
 *   actions={[
 *     {
 *       label: 'View All Countries',
 *       icon: <ListIcon />,
 *       onClick: () => navigate('/countries')
 *     },
 *     {
 *       label: 'Add New Country',
 *       icon: <AddIcon />,
 *       onClick: () => navigate('/countries/new')
 *     }
 *   ]}
 * />
 * ```
 */
const FormikAutocomplete = <T extends string | OptionType, TFormData extends Record<string, unknown>>({
  id,
  label,
  options,
  helperText,
  disabled = false,
  fullWidth = true,
  multiple = false,
  clearOnBlur = true,
  clearOnEscape = true,
  disableClearable = false,
  disableCloseOnSelect = false,
  disablePortal = false,
  filterOptions,
  getOptionLabel,
  isOptionEqualToValue,
  loading = false,
  optionsloading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
  renderOption,
  renderTags,
  size = 'medium',
  sx,
  slotProps,
  actions,
  onInputChange: propOnInputChange,
  onChange: propOnChange,
  initialOption,
  value,
  autoFocus = false,
  inputRef,
  testId: testIdProp,
  'data-testid': dataTestId,
  required,
  startAdornment,
  endAdornment,
  placeholder,
  skipValidationOnActionClick = false,
  ...rest
}: FormikAutocompleteProps<T, TFormData>) => {
  const { [TID_ATTR]: baseTestIdFromRest, ...restWithoutTestId } = rest as Record<string, unknown> & { [TID_ATTR]?: string };
  const baseTestId = testIdProp ?? dataTestId ?? baseTestIdFromRest;

  const { status, setFieldValue, setFieldTouched, errorText, hasError, fieldValue } = useFormikField<TFormData>({ id });
  const shouldSkipValidationOnActionClick = skipValidationOnActionClick || Boolean(actions?.length);
  const initialValueSet = useRef(false);
  const lastInitialOptionValue = useRef<string | number | undefined>(undefined);
  const actionClickedRef = useRef(false);

  useEffect(() => {
    const currentValue = getInitialOptionValue(initialOption);

    if (currentValue !== undefined) {
      if (lastInitialOptionValue.current !== undefined && lastInitialOptionValue.current !== currentValue) {
        initialValueSet.current = false;
      }
      lastInitialOptionValue.current = currentValue;
    }

    if (initialOption && !fieldValue && !initialValueSet.current && currentValue !== undefined) {
      initialValueSet.current = true;
      setFieldValue(id as keyof TFormData & string, currentValue);
    }
  }, [initialOption, fieldValue, setFieldValue, id]);

  // Find the current option(s) based on fieldValue
  const currentOption = useMemo<T | T[] | null>(() => {
    if (multiple) {
      // fieldValue is an array of primitive IDs — map each back to its option object
      if (!Array.isArray(fieldValue)) return [];
      return fieldValue
        .map(
          (fv: unknown) =>
            options.find((option) => {
              if (typeof option === 'object' && 'value' in option) return option.value === fv;
              return option === fv;
            }) ?? null
        )
        .filter((opt): opt is T => opt !== null);
    }

    if (!fieldValue) return null;

    // If initialOption is provided and matches the fieldValue, use it
    if (initialOption) {
      if (typeof initialOption === 'object' && 'value' in initialOption) {
        if (initialOption.value === fieldValue) return initialOption;
      } else if (initialOption === fieldValue) {
        return initialOption;
      }
    }

    // Otherwise look in options
    return (
      options.find((option) => {
        if (typeof option === 'object' && 'value' in option) {
          return option.value === fieldValue;
        }
        return option === fieldValue;
      }) || null
    );
  }, [fieldValue, multiple, options, initialOption]);

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: string, reason?: AutocompleteInputChangeReason) => {
      if (propOnInputChange) {
        propOnInputChange(event, value, reason);
      } else if (reason === 'input') {
        setFieldValue(id as keyof TFormData & string, value);
      }
    },
    [id, propOnInputChange, setFieldValue]
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: T | T[] | null) => {
      if (isOptionEqualToValue && value !== null && !Array.isArray(value)) {
        // Single selection: extract the primitive .value so Formik stores an ID,
        // not a full option object. currentOption then maps it back for display.
        const selectedValue = options.find((option) => isOptionEqualToValue(option, value));
        if (selectedValue && typeof selectedValue === 'object' && 'value' in selectedValue) {
          setFieldValue(id as keyof TFormData & string, selectedValue.value);
          propOnChange?.(selectedValue as T);
        } else {
          setFieldValue(id as keyof TFormData & string, value);
          propOnChange?.(value);
        }
      } else if (multiple && Array.isArray(value)) {
        // Multiple selection: extract primitive .value from each option object so
        // Formik stores an array of IDs. currentOption maps them back for chip display.
        // propOnChange still receives the full option objects for handlers that need them
        // (e.g. "Select All" expansion logic).
        const primitiveValues = value.map((v) => {
          if (typeof v === 'object' && v !== null && 'value' in v) {
            return (v as OptionType).value;
          }
          return v;
        });
        setFieldValue(id as keyof TFormData & string, primitiveValues);
        propOnChange?.(value);
      } else {
        setFieldValue(id as keyof TFormData & string, value);
        propOnChange?.(value);
      }
    },
    [id, isOptionEqualToValue, multiple, options, setFieldValue, propOnChange]
  );

  // Ensure unique keys for options
  const optionsWithKeys = useMemo(() => {
    return options.map((option, index) => {
      if (typeof option === 'object' && 'value' in option) {
        return {
          ...option,
          key: `${option.value}-${index}`
        } as T;
      }
      return {
        value: option,
        label: String(option),
        key: `${option}-${index}`
      } as T;
    });
  }, [options]);

  // Wrap actions to set flag before onClick (only if skipValidationOnActionClick is enabled)
  const wrappedActions = useMemo(() => {
    if (!actions || !shouldSkipValidationOnActionClick) return actions;
    return actions.map((action) => ({
      ...action,
      onClick: () => {
        actionClickedRef.current = true;
        action.onClick();
      }
    }));
  }, [actions, shouldSkipValidationOnActionClick]);

  const mergedSlotProps = useMemo(() => {
    const existing = (slotProps ?? {}) as Record<string, unknown>;
    const existingTextField = (existing.textField ?? {}) as {
      slotProps?: Record<string, unknown>;
      endAdornmentTestId?: Record<string, string>;
    };
    const existingTextFieldSlotProps = (existingTextField.slotProps ?? {}) as Record<string, Record<string, unknown> | undefined>;
    if (!baseTestId) return slotProps;
    const textFieldSlotProps = {
      inputLabel: { ...existingTextFieldSlotProps.inputLabel, [TID_ATTR]: `${baseTestId}:label` },
      htmlInput: { ...existingTextFieldSlotProps.htmlInput, [TID_ATTR]: `${baseTestId}:input` },
      formHelperText: { ...existingTextFieldSlotProps.formHelperText, [TID_ATTR]: `${baseTestId}:helper-text` }
    };
    const endAdornmentTestId = { [TID_ATTR]: `${baseTestId}:end_adornment` };
    return {
      ...existing,
      textField: {
        ...existingTextField,
        slotProps: textFieldSlotProps,
        endAdornmentTestId
      }
    };
  }, [baseTestId, slotProps]);

  const autocompleteProps = useMemo(
    () => ({
      id,
      options: optionsWithKeys,
      label,
      helperText: hasError ? errorText : (helperText ?? errorText),
      error: hasError,
      disabled,
      fullWidth,
      multiple,
      value: value || currentOption,
      onChange: handleChange,
      onInputChange: handleInputChange,
      onBlur: () => {
        if (!disabled) {
          if (shouldSkipValidationOnActionClick && actionClickedRef.current) {
            setTimeout(() => {
              actionClickedRef.current = false;
            }, 0);
          } else {
            setFieldTouched(id as keyof TFormData & string, true, true);
          }
        }
      },
      clearOnBlur,
      clearOnEscape,
      disableClearable,
      disableCloseOnSelect,
      disablePortal,
      filterOptions,
      getOptionLabel,
      isOptionEqualToValue,
      loading,
      optionsloading,
      loadingText,
      noOptionsText,
      renderOption,
      renderTags,
      size,
      sx,
      slotProps: mergedSlotProps,
      actions: wrappedActions,
      autoFocus,
      inputRef,
      required,
      startAdornment,
      endAdornment,
      placeholder,
      ...restWithoutTestId
    }),
    [
      id,
      optionsWithKeys,
      label,
      errorText,
      helperText,
      hasError,
      disabled,
      fullWidth,
      multiple,
      value,
      currentOption,
      handleChange,
      handleInputChange,
      setFieldTouched,
      clearOnBlur,
      clearOnEscape,
      disableClearable,
      disableCloseOnSelect,
      disablePortal,
      filterOptions,
      getOptionLabel,
      isOptionEqualToValue,
      loading,
      optionsloading,
      loadingText,
      noOptionsText,
      renderOption,
      renderTags,
      size,
      sx,
      mergedSlotProps,
      wrappedActions,
      autoFocus,
      inputRef,
      required,
      startAdornment,
      endAdornment,
      placeholder,
      shouldSkipValidationOnActionClick,
      restWithoutTestId
    ]
  );
  if (status?.loading || loading) {
    return <FormikFieldSkeleton />;
  }

  return <Autocomplete {...autocompleteProps} />;
};

export default FormikAutocomplete;
